"use client";
import { useState, useEffect } from "react";
import { FaGoogle } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { MdLogout } from "react-icons/md";

const GoogleAdsAccountModal = ({ isOpen, onClose, onAccountSelect, onDisconnect }) => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [saving, setSaving] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchAccounts();
        }
    }, [isOpen]);

    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/google-ads/accounts');
            const data = await response.json();
            
            if (data.success) {
                setAccounts(data.accounts || []);
            } else {
                console.error('Failed to fetch accounts:', data.error);
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAccountSelect = async () => {
        if (!selectedAccount) return;

        setSaving(true);
        try {
            const response = await fetch('/api/google-ads/accounts', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    adsAccountId: selectedAccount.customer_id
                }),
            });

            const data = await response.json();
            
            if (data.success) {
                onAccountSelect(selectedAccount);
                onClose();
            } else {
                console.error('Failed to save account:', data.error);
            }
        } catch (error) {
            console.error('Error saving account:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleDisconnect = async () => {
        if (!confirm('Are you sure you want to disconnect Google Ads? This will remove all saved data and you will need to reconnect.')) {
            return;
        }

        setDisconnecting(true);
        try {
            const response = await fetch('/api/integrations/google-ads/disconnect', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    hardDelete: true, // Delete record completely instead of soft delete
                    revokeToken: true
                }),
            });

            const data = await response.json();
            
            if (data.success) {
                if (onDisconnect) {
                    onDisconnect();
                }
                onClose();
            } else {
                alert('Failed to disconnect: ' + (data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error disconnecting:', error);
            alert('Failed to disconnect. Please try again.');
        } finally {
            setDisconnecting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <FaGoogle className="text-[#4285F4] text-xl" />
                        <h2 className="text-lg font-semibold text-gray-900">
                            Select Google Ads Account
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <IoClose className="w-6 h-6" />
                    </button>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                    Choose which Google Ads account you want to connect for tracking.
                </p>

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#284F9E]"></div>
                    </div>
                ) : accounts.length === 0 ? (
                    <div className="text-center py-8">
                        <p className="text-gray-500 mb-2">No regular Google Ads accounts found</p>
                        <p className="text-sm text-gray-400">Manager accounts cannot be used for metrics tracking or campaigns</p>
                    </div>
                ) : (
                    <div className="space-y-2 mb-6 max-h-60 overflow-y-auto">
                        {accounts.map((account) => (
                            <div
                                key={account.customer_id}
                                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                    selectedAccount?.customer_id === account.customer_id
                                        ? 'border-[#284F9E] bg-[#284F9E]/5'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                                onClick={() => setSelectedAccount(account)}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium text-gray-900">
                                            {account.name}
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            ID: {account.customer_id}
                                        </p>
                                        {account.currency && (
                                            <p className="text-xs text-gray-400">
                                                Currency: {account.currency}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center">
                                        {account.is_manager && (
                                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                Manager
                                            </span>
                                        )}
                                        <div className={`w-4 h-4 rounded-full border-2 ml-2 ${
                                            selectedAccount?.customer_id === account.customer_id
                                                ? 'border-[#284F9E] bg-[#284F9E]'
                                                : 'border-gray-300'
                                        }`}>
                                            {selectedAccount?.customer_id === account.customer_id && (
                                                <div className="w-full h-full rounded-full bg-white scale-50"></div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex flex-col gap-3">
                    {/* Disconnect button - always shown if connection exists */}
                    {!loading && (
                        <button
                            onClick={handleDisconnect}
                            disabled={disconnecting}
                            className="w-full px-4 py-2 text-red-600 border border-red-300 rounded-md hover:bg-red-50 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <MdLogout className="w-4 h-4" />
                            {disconnecting ? 'Disconnecting...' : 'Disconnect Google Ads'}
                        </button>
                    )}
                    
                    {/* Select account buttons - only show if accounts exist */}
                    {accounts.length > 0 && (
                        <div className="flex gap-3">
                            <button
                                onClick={onClose}
                                className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAccountSelect}
                                disabled={!selectedAccount || saving}
                                className="flex-1 px-4 py-2 bg-[#284F9E] text-white rounded-md hover:bg-[#1e3a7a] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? 'Saving...' : 'Select Account'}
                            </button>
                        </div>
                    )}
                    
                    {/* Cancel button if no accounts */}
                    {accounts.length === 0 && !loading && (
                        <button
                            onClick={onClose}
                            className="w-full px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Close
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GoogleAdsAccountModal;